import { useSeo } from "@/lib/seo";

import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { BadgeCheck, ThumbsUp, ThumbsDown, Meh, MapPin, Calendar, Repeat as RepeatIcon, Pencil, MailCheck, MailWarning, Loader2 } from "lucide-react";
import { api, imgSrc } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import ListingCard from "@/components/ListingCard";
import PushToggle from "@/components/PushToggle";
import EmailPrefsToggle from "@/components/EmailPrefsToggle";
import MutualConnections from "@/components/MutualConnections";
import VerificationBadges, { ReputationLine } from "@/components/VerificationBadges";
import VouchButton from "@/components/VouchButton";
import { toast } from "sonner";

export default function Profile() {
  const { id } = useParams();
  const { user: me, refreshUser, resendVerification } = useAuth();
  const userId = id || me?.user_id;
  const [profile, setProfile] = useState(null);
  const [ratings, setRatings] = useState([]);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ first_name: "", last_name: "", bio: "", location: "" });
  const [feedback, setFeedback] = useState(null);
  const [resending, setResending] = useState(false);

  const isMe = me && userId === me.user_id;

  const resend = async () => {
    setResending(true);
    try {
      const data = await resendVerification();
      toast.success(data?.message || "Verification email sent. Check your inbox.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send the verification email");
    } finally { setResending(false); }
  };

  const seoName = profile ? (`${profile.first_name || ""} ${profile.last_name || ""}`.trim() || profile.name || "Trader") : "";
  useSeo(profile && !isMe ? {
    title: `${seoName} — Trader Profile | Vyrti`,
    description: `See ${seoName}'s items for trade, completed swaps, vouches and trader reputation on Vyrti, the barter marketplace.`,
    path: `/profile/${profile.user_id}`,
    type: "profile",
  } : {});

  useEffect(() => {
    if (!userId) return;
    setLoading(true);
    Promise.all([
      api.get(`/users/${userId}`),
      api.get(`/users/${userId}/listings`),
      api.get(`/users/${userId}/feedback-summary`),
    ]).then(([p, l, f]) => {
      setProfile(p.data.user);
      setRatings(p.data.ratings || []);
      setListings(l.data.items || []);
      setFeedback(f.data);
      setForm({
        first_name: p.data.user.first_name || (p.data.user.name || "").split(" ")[0] || "",
        last_name:  p.data.user.last_name  || (p.data.user.name || "").split(" ").slice(1).join(" ") || "",
        bio: p.data.user.bio || "",
        location: p.data.user.location || ""
      });
    }).catch((e) => { console.warn("Profile load failed", e); })
      .finally(() => setLoading(false));
  }, [userId]);

  const saveProfile = async () => {
    try {
      const { data } = await api.patch("/auth/me", form);
      setProfile(data);
      await refreshUser();
      setEditing(false);
      toast.success("Profile updated");
    } catch (e) { toast.error("Could not update"); }
  };

  if (loading || !profile) {
    return <div className="section-pad py-8"><div className="skeleton h-40 max-w-3xl" /></div>;
  }

  const active = listings.filter(l => l.status === "active");
  const traded = listings.filter(l => l.status === "traded");

  return (
    <div className="section-pad py-8 md:py-12" data-testid="profile-page">
      {/* Header */}
      <div className="vyrti-card p-6 md:p-8">
        <div className="flex flex-col md:flex-row items-start gap-6">
          {profile.picture ? (
            <img src={imgSrc(profile.picture)} alt={profile.name} className="w-24 h-24 rounded-full object-cover" />
          ) : (
            <div className="w-24 h-24 rounded-full bg-[#F1F5F9] flex items-center justify-center font-display font-bold text-3xl">{profile.name?.[0]?.toUpperCase()}</div>
          )}
          <div className="flex-1">
            {editing ? (
              <div className="space-y-3 max-w-md">
                <div className="grid grid-cols-2 gap-3">
                  <input className="input-text" value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })} placeholder="First name" data-testid="edit-first-name" />
                  <input className="input-text" value={form.last_name}  onChange={e => setForm({ ...form, last_name:  e.target.value })} placeholder="Last name"  data-testid="edit-last-name" />
                </div>
                <input className="input-text" value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} placeholder="Location" data-testid="edit-location" />
                <textarea className="input-text min-h-[80px]" value={form.bio} onChange={e => setForm({ ...form, bio: e.target.value })} placeholder="About you…" data-testid="edit-bio" />
                <div className="flex gap-2">
                  <button onClick={saveProfile} className="btn-pill btn-accent !text-sm !py-2" data-testid="save-profile">Save</button>
                  <button onClick={() => setEditing(false)} className="btn-pill btn-soft !text-sm !py-2">Cancel</button>
                </div>
              </div>
            ) : (
              <>
                <h1 className="font-display font-bold text-3xl tracking-tight flex items-center gap-2">
                  {profile.name}
                  {profile.verified && <BadgeCheck className="w-6 h-6 text-[#0654BA]" />}
                </h1>
                <div className="mt-2 flex flex-wrap items-center gap-4 text-sm text-zinc-500">
                  {profile.location && <span className="flex items-center gap-1"><MapPin className="w-4 h-4" /> {profile.location}</span>}
                  <span className="flex items-center gap-1"><Calendar className="w-4 h-4" /> Joined {new Date(profile.created_at).toLocaleDateString()}</span>
                  {profile.email_verified && (
                    <span className="flex items-center gap-1 font-medium text-emerald-600" data-testid="profile-email-verified-badge"><MailCheck className="w-4 h-4" /> Email verified</span>
                  )}
                  {profile.rating_count > 0 && (
                    <span className="flex items-center gap-1 font-medium text-emerald-600"><ThumbsUp className="w-4 h-4" /> {Math.round(profile.positive_pct || 0)}% positive ({profile.rating_count})</span>
                  )}
                </div>
                {profile.bio && <p className="mt-3 text-zinc-600 max-w-xl">{profile.bio}</p>}
                {!isMe && (
                  <div className="mt-4 max-w-md">
                    <MutualConnections userId={profile.user_id} ownerName={profile.name} />
                  </div>
                )}
              </>
            )}
          </div>
          <div className="flex gap-2 items-start">
            <VouchButton userId={profile.user_id} ownerName={profile.first_name || profile.name} />
            {isMe && !editing && (
              <button onClick={() => setEditing(true)} className="btn-pill btn-soft !text-sm !py-2" data-testid="edit-profile">
                <Pencil className="w-4 h-4" /> Edit
              </button>
            )}
          </div>
        </div>

        {/* Notifications toggle */}
        {isMe && (
          <div className="mt-5 space-y-3">
            {!me.email_verified && (
              <div className="rounded-2xl border border-amber-300 bg-amber-50 p-4 flex items-center justify-between flex-wrap gap-3" data-testid="email-verify-banner">
                <div className="flex items-start gap-3">
                  <MailWarning className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-sm text-amber-900">Verify your email to earn a trust badge</p>
                    <p className="text-xs text-amber-800 mt-0.5">It shows traders you're the real deal and unlocks Trade Circles. Check your inbox for the link.</p>
                  </div>
                </div>
                <button onClick={resend} disabled={resending} className="btn-pill btn-accent !text-sm !py-2 disabled:opacity-50" data-testid="email-verify-resend-btn">
                  {resending ? <Loader2 className="w-4 h-4 animate-spin" /> : <MailCheck className="w-4 h-4" />}
                  {resending ? "Sending…" : "Resend email"}
                </button>
              </div>
            )}
            <div className="vyrti-card !shadow-none border border-[#E2E2E2] p-4 flex items-center justify-between flex-wrap gap-3">
              <div>
                <p className="font-medium text-sm">Match alerts</p>
                <p className="text-xs text-zinc-500 mt-0.5">Get a push the second someone posts what you've been hunting.</p>
              </div>
              <PushToggle />
            </div>
            <EmailPrefsToggle />
          </div>
        )}

        {/* Stats row — zero-value reputation metrics stay hidden until real */}
        <div className="mt-6 flex gap-3 max-w-md">
          <div className="flex-1 text-center bg-[#F7F7F7] rounded-2xl p-3" data-testid="profile-stat-active">
            <p className="font-display text-xl font-bold">{active.length}</p>
            <p className="text-xs text-zinc-500">Active</p>
          </div>
          {(profile.trades_completed || 0) > 0 && (
            <div className="flex-1 text-center bg-[#F7F7F7] rounded-2xl p-3" data-testid="profile-stat-trades-done">
              <p className="font-display text-xl font-bold">{profile.trades_completed}</p>
              <p className="text-xs text-zinc-500">Trades done</p>
            </div>
          )}
          {(profile.rating_count || 0) > 0 && (
            <div className="flex-1 text-center bg-[#F7F7F7] rounded-2xl p-3" data-testid="profile-stat-positive">
              <p className="font-display text-xl font-bold flex items-center justify-center gap-1 text-emerald-600">
                {Math.round(profile.positive_pct || 0)}%
                <ThumbsUp className="w-4 h-4" />
              </p>
              <p className="text-xs text-zinc-500">Positive</p>
            </div>
          )}
        </div>
      </div>

      {/* Listings */}
      <div className="mt-10">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display font-semibold text-2xl">{isMe ? "Your listings" : "Listings"}</h2>
          {isMe && (
            <Link to="/post" className="btn-pill btn-accent !text-sm !py-2" data-testid="profile-post">
              <RepeatIcon className="w-4 h-4" /> Post new
            </Link>
          )}
        </div>
        {active.length === 0 ? (
          <div className="vyrti-card p-10 text-center text-sm text-zinc-500" data-testid="profile-empty-listings">
            {isMe ? "You have no active listings. Post your first trade!" : "No active listings"}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {active.map(l => <div key={l.id} className="min-w-0"><ListingCard listing={l} compact />{isMe && <Link to={`/what-can-i-get?item_id=${l.id}`} className="mt-2 flex min-h-11 items-center justify-center rounded-md border border-[#0654BA] px-2 text-xs font-bold text-[#0654BA] hover:bg-[#E0EDFA]" data-testid={`my-listing-find-matches-${l.id}`}>Find matches</Link>}</div>)}
          </div>
        )}
      </div>

      {/* Traded — hidden until there's enough volume to look legit */}
      {traded.length >= 3 && (
        <div className="mt-10">
          <h2 className="font-display font-semibold text-2xl mb-4">Completed trades</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {traded.map(l => <ListingCard key={l.id} listing={l} compact />)}
          </div>
        </div>
      )}

      {/* Ratings */}
      {ratings.length > 0 && (
        <div className="mt-10">
          <h2 className="font-display font-semibold text-2xl mb-4">Reviews</h2>
          <div className="space-y-3 max-w-2xl">
            {ratings.map(r => {
              const Icon = r.sentiment === "positive" ? ThumbsUp : r.sentiment === "negative" ? ThumbsDown : Meh;
              const iconColor = r.sentiment === "positive" ? "text-emerald-600" : r.sentiment === "negative" ? "text-rose-600" : "text-amber-600";
              const bg = r.sentiment === "positive" ? "bg-emerald-50" : r.sentiment === "negative" ? "bg-rose-50" : "bg-amber-50";
              return (
                <div key={r.id} className="vyrti-card p-4">
                  <div className="flex items-center gap-3">
                    {r.from_user?.picture ? (
                      <img src={imgSrc(r.from_user.picture)} alt="" className="w-8 h-8 rounded-full object-cover" />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-[#F1F5F9] flex items-center justify-center text-xs font-semibold">{r.from_user?.name?.[0]?.toUpperCase()}</div>
                    )}
                    <p className="text-sm font-medium flex-1">{r.from_user?.name}</p>
                    <span className={`flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full ${bg} ${iconColor}`}>
                      <Icon className="w-3.5 h-3.5" /> {r.sentiment || (r.stars >= 4 ? "positive" : r.stars === 3 ? "neutral" : "negative")}
                    </span>
                  </div>
                  {r.comment && <p className="text-sm text-zinc-600 mt-2">{r.comment}</p>}
                  <p className="text-[11px] text-zinc-400 mt-2">{new Date(r.created_at).toLocaleDateString()}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
